<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="sa-nw-reference-modal"
    title="CPA 5G SA設定情報"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col class="d-flex flex-row ga-2 justify-end mb-3">
                <!-- 約款参照 -->
                <v-btn color="primary" @click="confirmTerms"> 約款参照 </v-btn>
                <template
                  v-if="
                    (cpaContractInfo.status === 'ACTIVE_UPDATE' ||
                      cpaContractInfo.status === 'INACTIVE') &&
                    isUpdate()
                  "
                >
                  <v-btn
                    v-permission.config.delegate
                    color="info"
                    @click="settingUpdate"
                  >
                    データ反映
                  </v-btn>
                </template>
                <!-- 認証ID登録 -->
                <template
                  v-if="
                    (cpaContractInfo.status === 'ACTIVE' ||
                      cpaContractInfo.status === 'ACTIVE_UPDATE') &&
                    cpaContractInfo.authenticationType !== 'HOMERADIUS'
                  "
                >
                  <v-btn v-permission.admin color="primary" @click="idRegister">
                    認証ID登録
                  </v-btn>
                </template>
                <!-- 認証ID情報取得 -->
                <v-btn color="primary" @click="showAuthCpaIdInfo">
                  認証ID情報画面
                </v-btn>
                <!-- NW設定 -->
                <v-btn
                  v-permission.config.delegate
                  color="info"
                  @click="cpaNwSetting"
                >
                  NW設定
                </v-btn>
                <!-- 解約 -->
                <v-btn
                  v-permission.admin.delegate
                  color="info"
                  @click="terminateContract"
                >
                  解約
                </v-btn>
              </v-col>
            </v-row>

            <div>
              <!-- 契約情報 : 契約情報、基本情報、NW設定状況 -->
              <sa-nw-contract-info :cpaContractInfo="cpaContractInfo" />

              <!-- 設定情報 : NW設定共通、 NW設定(ACT・SBY)、保守連絡先、その他項目、テストアカウント -->
              <sa-nw-setting-info
                v-if="cpaContractInfo.wnumber"
                :cpaContractInfo="cpaContractInfo"
                :nwSettingDetail="nwSettingDetail"
                :nwOptions="nwOptions"
                :isUpdateRequiredRed="true"
                :isSortUeIpAllocationIpv4List="true"
              />
            </div>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import SaNwEdit, { createCpaNwPostObj } from "@/modals/cpa5g/SaNwEdit.vue";
import type {
  CpaContract,
  CpaNwDetail,
  CpaNwOptions,
  UpdateCpaNw,
} from "@/apis/Cpa5gSaApi";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import SaIdRegister from "@/modals/cpa5g/SaIdRegister.vue";
import SaNwContractInfo from "@/components/cpa5gSa/SaNwContractInfo.vue";
import SaNwSettingInfo from "@/components/cpa5gSa/SaNwSettingInfo.vue";
import SaNwContractDeleteConfirm from "./SaNwContractDeleteConfirm.vue";
import SaNwDataUpdateConfirm from "@/modals/cpa5g/SaNwDataUpdateConfirm.vue";
import SaContractTermsConfirm from "@/modals/cpa5g/SaContractTermsConfirm.vue";

export default defineComponent({
  name: "SaNwReference",
  components: { SaNwContractInfo, SaNwSettingInfo },
  props: {
    // CPA NW契約情報
    cpaContractInfo: {
      type: Object as PropType<CpaContract>,
      required: true,
    },
  },
  data() {
    return {
      termsDownloadUrl: this.$api.cpa5gSa.downloadContractTermsPdfURL(),
      nwSettingDetail: {} as CpaNwDetail,
      nwOptions: {} as CpaNwOptions,
      isLoaded: false,
    };
  },
  async mounted() {
    this.nwSettingDetail = await this.$api.cpa5gSa.getCpaNwDetail(
      this.cpaContractInfo.cpaContractSeq
    );
    this.nwOptions = await this.$api.cpa5gSa.getCpaNwOptions(
      this.cpaContractInfo.cpaContractSeq
    );
    this.isLoaded = true;
  },
  methods: {
    isUpdate() {
      return Object.values(this.nwOptions).includes("UPDATE");
    },

    // 約款参照
    async confirmTerms() {
      await this.$modal.show(SaContractTermsConfirm, {
        isConfirmationMode: false,
      });
    },

    /** データ反映 */
    async settingUpdate() {
      await this.$modal.show(SaNwDataUpdateConfirm, {
        cpaContractInfo: this.cpaContractInfo,
        nwSettingDetail: this.nwSettingDetail,
        nwOptions: this.nwOptions,
      });
      // APIに設定するデータを作成
      const nwPostData: UpdateCpaNw = createCpaNwPostObj(this.nwSettingDetail);

      // NW設定の登録・更新API呼び出し
      await this.$api.cpa5gSa.updateCpaNw(
        this.cpaContractInfo.cpaContractSeq,
        nwPostData
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** 認証ID情報取得 */
    async showAuthCpaIdInfo() {
      await this.$router.push({
        path: "/report/remote/access/account",
      });
    },

    /** 契約解約 */
    async terminateContract() {
      if (this.isLoaded) {
        await this.$modal.show(SaNwContractDeleteConfirm, {
          cpaContractInfo: this.cpaContractInfo,
          nwSettingDetail: this.nwSettingDetail,
          nwOptions: this.nwOptions,
        });
        await this.$api.cpa5gSa.terminateCpaContract(
          this.cpaContractInfo.cpaContractSeq,
          {
            cpaContractKeyId: this.cpaContractInfo.cpaContractKeyId,
          }
        );
        // 解約後には設定情報画面も閉じる。(一覧画面で再読み込みを行うためOKとする)
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      }
    },

    /** 認証ID登録 */
    async idRegister() {
      await this.$modal.show(SaIdRegister, {
        cpaContractSeq: this.cpaContractInfo.cpaContractSeq,
        contractType: this.cpaContractInfo.contractType,
      });
    },

    /** NW設定 */
    async cpaNwSetting() {
      await this.$modal.show(SaNwEdit, {
        cpaContract: this.cpaContractInfo,
        cpaNw: this.nwSettingDetail,
        cpaNwOption: this.nwOptions,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
<style lang="scss">
// CPA 5G SA参照画面特殊サイズダイアログ
.sa-nw-reference-modal {
  width: 1000px;

  .modal-body {
    max-height: 70vh;
    overflow: auto;
  }
}
</style>
